import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, Trash2, FileText, Save, Search, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function Vales() {
  const [tab, setTab] = useState('captura');
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vales, setVales] = useState([]);

  // Form state
  const [numeroVale, setNumeroVale] = useState('');
  const [repartidorId, setRepartidorId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState([{ group_code: '', cantidad: '', observaciones: '' }]);
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientOpen, setClientOpen] = useState(false);

  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients;
    const search = clientSearch.toLowerCase();
    return clients.filter(c => c.nombre.toLowerCase().includes(search) || c.tipo_cliente.toLowerCase().includes(search));
  }, [clients, clientSearch]);

  const selectedClient = clients.find(c => c.id === clienteId);

  const fetchCatalogs = async () => {
    try {
      const [d, c, g, w] = await Promise.all([
        API.get('/catalogs/drivers'),
        API.get('/catalogs/clients'),
        API.get('/catalogs/groups'),
        API.get('/catalogs/warehouses')
      ]);
      setDrivers(d.data);
      setClients(c.data);
      setGroups(g.data);
      setWarehouses(w.data);
    } catch { toast.error('Error cargando catálogos'); }
  };

  const fetchVales = async () => {
    try {
      const res = await API.get('/vales');
      setVales(res.data);
    } catch { toast.error('Error cargando vales'); }
  };

  useEffect(() => { fetchCatalogs(); fetchVales(); }, []);

  const addLine = () => setLineas([...lineas, { group_code: '', cantidad: '', observaciones: '' }]);
  const removeLine = (idx) => setLineas(lineas.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) => {
    const updated = [...lineas];
    updated[idx][field] = value;
    setLineas(updated);
  };

  const handleSave = async () => {
    if (!numeroVale.trim()) { toast.error('Ingresa número de vale'); return; }
    if (!repartidorId) { toast.error('Selecciona repartidor'); return; }
    if (!clienteId) { toast.error('Selecciona cliente'); return; }
    const validLines = lineas.filter(l => l.group_code && parseInt(l.cantidad) > 0);
    if (validLines.length === 0) { toast.error('Agrega al menos una línea con grupo y cantidad'); return; }

    setSaving(true);
    try {
      const res = await API.post('/vales', {
        numero_vale: numeroVale,
        repartidor_id: repartidorId,
        cliente_id: clienteId,
        observaciones,
        lineas: validLines.map(l => ({ group_code: l.group_code, cantidad: parseInt(l.cantidad), observaciones: l.observaciones }))
      });
      if (res.data.warning) toast.warning(res.data.warning);
      toast.success(`Vale ${res.data.folio_interno} registrado exitosamente`);
      // Reset form
      setNumeroVale(''); setRepartidorId(''); setClienteId('');
      setObservaciones(''); setLineas([{ group_code: '', cantidad: '', observaciones: '' }]);
      fetchVales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar vale');
    } finally { setSaving(false); }
  };

  const totalPiezas = lineas.reduce((sum, l) => sum + (parseInt(l.cantidad) || 0), 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Vales</h1>
        <p className="text-sm text-muted-foreground">Captura e historial de vales de acumuladores usados</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="captura">Captura de Vale</TabsTrigger>
          <TabsTrigger value="historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="captura" className="space-y-4 mt-4">
          {/* Header Card */}
          <Card className="bg-white border">
            <CardHeader className="pb-3"><CardTitle className="text-base">Datos del Vale</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-sm font-medium">Número de Vale *</label>
                  <Input value={numeroVale} onChange={(e) => setNumeroVale(e.target.value)} placeholder="Ej: V-001" className="mt-1" data-testid="field-numero-vale" />
                </div>
                <div>
                  <label className="text-sm font-medium">Repartidor *</label>
                  <Select value={repartidorId} onValueChange={setRepartidorId}>
                    <SelectTrigger className="mt-1" data-testid="field-repartidor"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Cliente *</label>
                  <Popover open={clientOpen} onOpenChange={setClientOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full mt-1 justify-between font-normal" data-testid="field-cliente">
                        {selectedClient ? (
                          <span className="truncate">{selectedClient.nombre} {selectedClient.tipo_cliente !== 'general' ? `(${selectedClient.tipo_cliente})` : ''}</span>
                        ) : (
                          <span className="text-muted-foreground">Buscar cliente...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="start">
                      <div className="p-2 border-b">
                        <div className="flex items-center gap-2 px-2">
                          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                          <input
                            className="flex-1 py-1.5 text-sm outline-none bg-transparent placeholder:text-muted-foreground"
                            placeholder="Escribir nombre..."
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-[250px] overflow-y-auto p-1">
                        {filteredClients.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No se encontraron clientes</p>
                        )}
                        {filteredClients.map(c => (
                          <button key={c.id}
                            className={cn("w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-muted text-left",
                              clienteId === c.id && "bg-[#0F6E87]/10")}
                            onClick={() => { setClienteId(c.id); setClientOpen(false); setClientSearch(''); }}>
                            <Check className={cn("h-3.5 w-3.5 shrink-0", clienteId === c.id ? "opacity-100 text-[#0F6E87]" : "opacity-0")} />
                            <span className="flex-1 truncate">{c.nombre}</span>
                            {c.tipo_cliente !== 'general' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">{c.tipo_cliente}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm font-medium">Observaciones</label>
                  <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Opcional" className="mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detail Lines */}
          <Card className="bg-white border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Detalle de Acumuladores</CardTitle>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold">Total: {totalPiezas} pzas</span>
                <Button size="sm" variant="outline" onClick={addLine}><Plus className="h-3.5 w-3.5 mr-1" /> Línea</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Grupo *</TableHead>
                    <TableHead>Cantidad *</TableHead>
                    <TableHead>Observaciones</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineas.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell>
                        <Select value={line.group_code} onValueChange={(v) => updateLine(idx, 'group_code', v)}>
                          <SelectTrigger><SelectValue placeholder="Grupo" /></SelectTrigger>
                          <SelectContent>{groups.map(g => <SelectItem key={g.codigo} value={g.codigo}>{g.codigo} ({g.capacidad_tarima} pzas)</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input type="number" min="1" value={line.cantidad} onChange={(e) => updateLine(idx, 'cantidad', e.target.value)} placeholder="0" />
                      </TableCell>
                      <TableCell>
                        <Input value={line.observaciones} onChange={(e) => updateLine(idx, 'observaciones', e.target.value)} placeholder="Opcional" />
                      </TableCell>
                      <TableCell>
                        {lineas.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeLine(idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]" data-testid="vales-create-button">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : 'Registrar Vale'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>No. Vale</TableHead><TableHead>Repartidor</TableHead>
                <TableHead>Cliente</TableHead><TableHead>Almacén</TableHead>
                <TableHead className="text-right">Piezas</TableHead><TableHead>Fecha</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {vales.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No hay vales registrados</TableCell></TableRow>}
                {vales.map(v => (
                  <TableRow key={v.id} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{v.folio_interno}</TableCell>
                    <TableCell className="font-medium">{v.numero_vale}</TableCell>
                    <TableCell>{v.repartidor_nombre}</TableCell>
                    <TableCell>{v.cliente_nombre}</TableCell>
                    <TableCell>{v.warehouse_nombre}</TableCell>
                    <TableCell className="text-right font-semibold">{v.total_piezas}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(v.fecha).toLocaleDateString('es-MX')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
